/**
 * Enhanced Due Diligence Mock Data Panel
 *
 * 开发环境下的快捷上传工具
 * 提供可视化的面板用于快速上传加强尽职调查文件
 *
 * @module mockDataGenerator.demo
 */

import { delay } from "@/utils";
import { triggerFileUpload } from "../utils/mockFileUploadTrigger";
import { Button, InputNumber, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { useState } from "react";

interface EnhancedDueDiligenceMockDataPanelProps {
  form: FormInstance;
}

/**
 * 加强尽职调查 Mock 数据生成工具面板
 */
export function EnhancedDueDiligenceMockDataPanel({
  form,
}: EnhancedDueDiligenceMockDataPanelProps) {
  const [visible, setVisible] = useState(false);
  const [uploading, setUploading] = useState(false);

  // 文件数量配置
  const [fundSourcesCount, setFundSourcesCount] = useState(3);
  const [wealthSourcesCount, setWealthSourcesCount] = useState(3);
  const [othersCount, setOthersCount] = useState(2);

  if (!form) {
    return null;
  }

  /**
   * 上传一次文件，设置给所有字段
   */
  const handleUploadFiles = async () => {
    setUploading(true);
    message.loading({
      content: "正在上传文件...",
      key: "upload-files",
      duration: 0,
    });

    try {
      // 步骤1：触发一次上传
      console.log("🔄 触发文件上传...");
      await triggerFileUpload({
        fileUrl: "/icon.png",
        fileName: "edd-document.png",
        selector: 'input[type="file"]',
      });

      // 步骤2：等待上传完成
      message.loading({
        content: "等待上传完成...",
        key: "upload-files",
        duration: 0,
      });

      let uploadedFile: any = null;
      const maxAttempts = 20;

      for (let i = 0; i < maxAttempts; i++) {
        await delay(500);

        // 从第一个字段获取上传的文件
        const currentFiles = form.getFieldValue("fundSources");
        if (
          currentFiles &&
          currentFiles.length > 0 &&
          currentFiles[0]?.fileId
        ) {
          uploadedFile = currentFiles[0];
          console.log("✅ 上传成功，fileId:", uploadedFile.fileId);
          break;
        }

        console.log(`⏳ 等待上传完成... (${i + 1}/${maxAttempts})`);
      }

      if (!uploadedFile || !uploadedFile.fileId) {
        throw new Error("上传超时，未获取到fileId");
      }

      // 步骤3：设置给所有字段
      message.loading({
        content: "正在设置文件...",
        key: "upload-files",
        duration: 0,
      });

      form.setFieldsValue({
        fundSources: Array(fundSourcesCount).fill(uploadedFile),
        wealthSources: Array(wealthSourcesCount).fill(uploadedFile),
        others: Array(othersCount).fill(uploadedFile),
      });

      const total = fundSourcesCount + wealthSourcesCount + othersCount;
      message.success({
        content: `✅ 已设置 ${total} 个文件`,
        key: "upload-files",
        duration: 3,
      });
      console.log(`✅ 已设置 ${total} 个文件，fileId:`, uploadedFile.fileId);
    } catch (error) {
      message.error({
        content: "❌ 上传失败",
        key: "upload-files",
        duration: 3,
      });
      console.error("❌ 上传失败:", error);
    } finally {
      setUploading(false);
    }
  };

  const handleToggle = () => {
    setVisible(!visible);
  };

  return (
    <>
      {/* 浮动按钮 */}
      {!visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] group cursor-pointer"
          onClick={handleToggle}
          style={{
            width: "64px",
            height: "64px",
          }}
        >
          <div
            className="absolute inset-0 rounded-full transition-all duration-300 group-hover:scale-110"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              boxShadow:
                "0 10px 25px -5px rgba(102, 126, 234, 0.5), 0 8px 10px -6px rgba(102, 126, 234, 0.3)",
            }}
          />
          <div
            className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            style={{
              background: "linear-gradient(135deg, #764ba2 0%, #667eea 100%)",
              boxShadow: "0 15px 35px -5px rgba(118, 75, 162, 0.6)",
            }}
          />
          <div className="relative w-full h-full flex items-center justify-center text-3xl transition-transform duration-300 group-hover:scale-110 group-active:scale-95">
            📋
          </div>
          <div
            className="absolute inset-0 rounded-full animate-ping opacity-20"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              animationDuration: "3s",
            }}
          />
        </div>
      )}

      {/* 主面板 */}
      {visible && (
        <div
          className="fixed bottom-8 right-8 z-[9999] bg-white rounded-lg shadow-2xl"
          style={{
            width: "320px",
            maxHeight: "600px",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #d9d9d9",
          }}
        >
          {/* 标题栏 */}
          <div
            className="flex items-center justify-between px-4 py-3"
            style={{
              borderBottom: "1px solid #f0f0f0",
              backgroundColor: "#fafafa",
              borderRadius: "8px 8px 0 0",
            }}
          >
            <span className="font-semibold text-base">
              📋 加强尽职调查 Mock工具
            </span>
            <Button size="small" onClick={handleToggle}>
              ✕
            </Button>
          </div>

          {/* 内容区域 */}
          <div
            className="overflow-y-auto p-4 space-y-4"
            style={{ maxHeight: "500px" }}
          >
            {/* 文件数量配置 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                🔢 文件数量配置
              </div>

              {/* 资金来源证明 */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">资金来源证明</span>
                <InputNumber
                  size="small"
                  min={0}
                  max={10}
                  value={fundSourcesCount}
                  onChange={(val) => setFundSourcesCount(val || 0)}
                  style={{ width: "70px" }}
                />
              </div>

              {/* 财富来源证明 */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">财富来源证明</span>
                <InputNumber
                  size="small"
                  min={0}
                  max={10}
                  value={wealthSourcesCount}
                  onChange={(val) => setWealthSourcesCount(val || 0)}
                  style={{ width: "70px" }}
                />
              </div>

              {/* 其他文件 */}
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-600">其他文件</span>
                <InputNumber
                  size="small"
                  min={0}
                  max={10}
                  value={othersCount}
                  onChange={(val) => setOthersCount(val || 0)}
                  style={{ width: "70px" }}
                />
              </div>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 文件上传操作 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                📤 文件上传
              </div>
              <Button
                size="small"
                block
                type="primary"
                onClick={handleUploadFiles}
                loading={uploading}
              >
                上传文件
              </Button>
            </div>

            {/* 分割线 */}
            <div className="border-t border-gray-200" />

            {/* 说明 */}
            <div className="space-y-2">
              <div className="text-sm font-semibold text-gray-700">
                ℹ️ 使用说明
              </div>
              <div className="text-xs text-gray-600 space-y-1">
                <div>• 在上方设置每个字段需要几个文件</div>
                <div>• 点击"上传文件"一次性设置所有字段</div>
                <div>• 只上传一张图片，然后复用给所有字段</div>
                <div>• 所有文件都使用同一张图片（/icon.png）</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default EnhancedDueDiligenceMockDataPanel;
